// src/modules/analytics/components/TokensBarChart.tsx
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { LlmUsageStatItem } from '../types';

interface ChartRow {
  date: string;
  Вход: number;
  Выход: number;
  action_count: number;
  response_time_avg: number;
}

function formatDateLabel(dateStr: string): string {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}`;
}

function formatTokensAxis(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return String(value);
}

// Цвета серий завязаны на shell-палитру (graphite + emerald):
// вход — emerald-акцент, выход — graphite, чтобы стек читался в обеих темах
function useChartColors() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return {
    in: dark ? '#34D399' : '#10B981',
    out: dark ? '#6D6F76' : '#5A5C63',
    grid: theme.palette.divider,
    tick: theme.palette.text.secondary,
    cursor: alpha(theme.palette.text.primary, dark ? 0.06 : 0.04),
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const row = (payload[0] as unknown as { payload: ChartRow })?.payload;
  return (
    <Box
      sx={{
        background: (t) => t.palette.background.paper,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        fontSize: '0.78rem',
        boxShadow: (t) =>
          t.palette.mode === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.5)'
            : '0 4px 12px rgba(15,16,20,0.06)',
        color: 'text.primary',
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.8rem' }}>{label}</Typography>
      {payload.map((entry) => (
        <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: 0.5,
                background: entry.color,
                flexShrink: 0,
              }}
            />
            <span>{entry.name}</span>
          </Box>
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {entry.value.toLocaleString('ru')}
          </Box>
        </Box>
      ))}
      <Box
        sx={{
          mt: 0.5,
          pt: 0.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          color: 'text.secondary',
        }}
      >
        <span>Вызовов:</span>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {row?.action_count ?? 0}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          color: 'text.secondary',
        }}
      >
        <span>Avg ответ:</span>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {row?.response_time_avg ?? 0}ms
        </Box>
      </Box>
    </Box>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: 0.5, background: color }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

interface Props {
  data: LlmUsageStatItem[];
  loading: boolean;
}

export function TokensBarChart({ data, loading }: Props) {
  const colors = useChartColors();

  const chartData: ChartRow[] = data.map((item) => ({
    date: formatDateLabel(item.date),
    Вход: item.tokens_in_total,
    Выход: item.tokens_out_total,
    action_count: item.action_count,
    response_time_avg: item.response_time_avg,
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          Использование токенов по дням
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <LegendDot color={colors.in} label="Вход" />
          <LegendDot color={colors.out} label="Выход" />
        </Box>
      </Box>
      {loading ? (
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} />
      ) : data.length === 0 ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Нет данных за выбранный период
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barSize={20}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: colors.tick }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatTokensAxis}
              tick={{ fontSize: 11, fill: colors.tick }}
              axisLine={false}
              tickLine={false}
              width={42}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: colors.cursor }} />
            <Bar dataKey="Вход" stackId="tokens" fill={colors.in} />
            <Bar dataKey="Выход" stackId="tokens" fill={colors.out} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}
